/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { DocumentTagCreate } from '../models/DocumentTagCreate';
import type { DocumentTagRead } from '../models/DocumentTagRead';
import type { DocumentTagUpdate } from '../models/DocumentTagUpdate';
import type { DocumentToAnalyze } from '../models/DocumentToAnalyze';
import type { PaginatedResult } from '../models/PaginatedResult';
import type { SuggestedDocumentTag } from '../models/SuggestedDocumentTag';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class DocumentTagsService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * List Document Tags By Namespace Id
     * @returns DocumentTagRead Successful Response
     * @throws ApiError
     */
    public listDocumentTagsByNamespaceId(): CancelablePromise<Record<string, Array<DocumentTagRead>>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/document-tags/list-document-tags-by-namespace-id/',
        });
    }

    /**
     * List Document Tags For Namespace Id
     * @param namespaceId
     * @returns DocumentTagRead Successful Response
     * @throws ApiError
     */
    public listDocumentTagsForNamespaceId(
        namespaceId: string,
    ): CancelablePromise<Array<DocumentTagRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/document-tags/list-document-tags-for-namespace-id/{namespace_id}',
            path: {
                'namespace_id': namespaceId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Document Tag
     * @param documentTagId
     * @returns any Successful Response
     * @throws ApiError
     */
    public deleteDocumentTag(
        documentTagId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/document-tags/delete-document-tag/{document_tag_id}',
            path: {
                'document_tag_id': documentTagId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Document Tag
     * @param namespaceId
     * @param requestBody
     * @returns DocumentTagRead Successful Response
     * @throws ApiError
     */
    public createDocumentTag(
        namespaceId: string,
        requestBody: DocumentTagCreate,
    ): CancelablePromise<DocumentTagRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/document-tags/create-document-type/{namespace_id}',
            path: {
                'namespace_id': namespaceId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Document Type
     * @param namespaceId
     * @param documentTagId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public updateDocumentType(
        namespaceId: string,
        documentTagId: string,
        requestBody: DocumentTagUpdate,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/document-tags/update-document-type/{namespace_id}/{document_tag_id}',
            path: {
                'namespace_id': namespaceId,
                'document_tag_id': documentTagId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Audit Logs For Document Tag
     * @param documentTagId
     * @param cursor
     * @param pageSize
     * @returns PaginatedResult Successful Response
     * @throws ApiError
     */
    public listAuditLogsForDocumentTag(
        documentTagId: string,
        cursor?: (string | null),
        pageSize: number = 20,
    ): CancelablePromise<PaginatedResult> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/document-tags/list-audit-logs-for-document-tag/{document_tag_id}',
            path: {
                'document_tag_id': documentTagId,
            },
            query: {
                'cursor': cursor,
                'page_size': pageSize,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Suggested Document Tags For Item Names
     * Get suggested document tags for a document.
     * @param namespaceId
     * @param requestBody
     * @returns SuggestedDocumentTag Successful Response
     * @throws ApiError
     */
    public getSuggestedDocumentTagsForItemNames(
        namespaceId: string,
        requestBody: Array<string>,
    ): CancelablePromise<Record<string, Array<SuggestedDocumentTag>>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/document-tags/get-suggested-document-tags-for-item-names/{namespace_id}',
            path: {
                'namespace_id': namespaceId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Analyze Documents For Suggestions
     * Dispatch async document analysis for tag and effective date suggestions.
     *
     * Results delivered via WebSocket channel: document_tag_suggestions:{user_id}:{request_id}
     * @param namespaceId
     * @param requestId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public analyzeDocumentsForSuggestions(
        namespaceId: string,
        requestId: string,
        requestBody: Array<DocumentToAnalyze>,
    ): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/document-tags/analyze-documents-for-suggestions/{namespace_id}',
            path: {
                'namespace_id': namespaceId,
            },
            query: {
                'request_id': requestId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Document Tags For Cluster
     * @param clusterId
     * @returns DocumentTagRead Successful Response
     * @throws ApiError
     */
    public listDocumentTagsForCluster(
        clusterId: string,
    ): CancelablePromise<Record<string, Array<DocumentTagRead>>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/clusters/{cluster_id}/document-tags/list-document-tags-for-cluster',
            path: {
                'cluster_id': clusterId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

}
